import React, { useRef, useContext } from 'react'
import { FixedSizeList as List } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import { bool, func, number, string } from 'prop-types'
import { productsDataType } from 'models/propTypes'
import classNames from 'classnames'

import {
    MY_ITEMS_BROWSE_NUM_COLUMNS,
    FURNITURE_BROWSE_NUM_COLUMNS,
    FURNITURE_SWAP_NUM_COLUMNS,
} from 'models/ui/constants'
import { TRACKING_ADDED_FROM_CATALOG } from 'models/tracking/constants'

import ProductItemContainer from 'containers/designSidepanel/ProductItemContainer'
import BouncingBallLoader from 'components/uiElements/BouncingBallLoader'

import SugarcubeContext from 'apps/SugarcubeContext'

require('./Products.scss')

const ITEM_HEIGHT_BY_COL = {
    [MY_ITEMS_BROWSE_NUM_COLUMNS]: 200,
    [FURNITURE_BROWSE_NUM_COLUMNS]: 280,
    [FURNITURE_SWAP_NUM_COLUMNS]: 280,
}
const ITEM_WIDTH = 150

const NEXT_PAGE_TRIGGER = 1000
const DEFAULT_LIST_LAYOUT = 'vertical'

const Products = ({ furnituresData, furnitureIsLoading, requestFurniture, columnCount, listLayout }) => {
    const { isMobile } = useContext(SugarcubeContext)
    const itemSize = isMobile ? ITEM_WIDTH : ITEM_HEIGHT_BY_COL[columnCount]
    const actualColumnCount = isMobile ? 1 : columnCount

    const containerHeightRef = useRef()
    if (!(furnituresData && furnituresData.length)) {
        if (furnitureIsLoading) {
            return <BouncingBallLoader />
        }
        return null
    }

    const onScroll = ({ scrollOffset }) => {
        if (containerHeightRef.current) {
            const loadTrigger = (furnituresData.length / actualColumnCount) * itemSize - NEXT_PAGE_TRIGGER
            const scrollProgress = scrollOffset + containerHeightRef.current.clientHeight
            if (scrollProgress > loadTrigger && !furnitureIsLoading && requestFurniture) {
                requestFurniture()
            }
        }
    }

    return (
        <div style={{ height: '100%', width: '100%' }} className="Products" ref={containerHeightRef}>
            <AutoSizer>
                {({ height, width }) => (
                    <List
                        height={height}
                        itemCount={Math.ceil(furnituresData.length / actualColumnCount)}
                        itemSize={itemSize}
                        width={width}
                        onScroll={onScroll}
                        layout={listLayout}
                    >
                        {({ index, style }) => {
                            // This returns the content of each row in the list.
                            // The itemCount prop in the List component = the number of rows
                            // We ensure that we will not have have an out of bounds index by
                            // using Math.ceil().
                            // if actualColumnCount is 2, Ex: 11 items --> Math.ceil(11/2) = 6 rows
                            const colArray = [...Array(actualColumnCount).keys()]
                            const furnitureRowArray = colArray.map((colIndex) => {
                                const furnitureDataIndex = actualColumnCount * index + colIndex

                                return furnituresData[furnitureDataIndex]
                            })

                            return (
                                <div style={style}>
                                    <div className={classNames('furnitureListRow', { mobile: isMobile })}>
                                        {furnitureRowArray.map((data) =>
                                            data ? (
                                                <ProductItemContainer
                                                    furnitureData={data}
                                                    key={data.uniqueId || data.id}
                                                    trackingInfoAddedFrom={TRACKING_ADDED_FROM_CATALOG}
                                                />
                                            ) : null,
                                        )}
                                    </div>
                                </div>
                            )
                        }}
                    </List>
                )}
            </AutoSizer>
        </div>
    )
}

Products.defaultProps = {
    furnitureIsLoading: false,
    requestFurniture: null,
    columnCount: FURNITURE_BROWSE_NUM_COLUMNS,
    furnituresData: [],
    listLayout: DEFAULT_LIST_LAYOUT,
}

Products.propTypes = {
    furnituresData: productsDataType,
    furnitureIsLoading: bool,
    requestFurniture: func,
    columnCount: number,
    listLayout: string,
}

export default Products
